import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Modal, ModalContent } from "@/components/ui/Modal";
import { Loader2, X } from "lucide-react";
import FieldError from "@/components/Error/field-error";
import { usePackagingTypes } from "@/hooks/usePackagingTypes";
import { useRecipes } from "@/hooks/useRecipes";

const packagingStatusOptions = [
    { label: "Not Started", value: "Not Started" },
    { label: "In Progress", value: "In Progress" },
    { label: "Completed", value: "Completed" },
    { label: "Paused", value: "Paused" },
    { label: "Cancelled", value: "Cancelled" },
];

export function SampleModal({
    open,
    onOpenChange,
    sample,
    onConfirm,
    mode = "update",
    projectId,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { data: packagingTypesData, isLoading: packagingTypesLoading } = usePackagingTypes({ status: "active" });
    const { data: recipesData, isLoading: recipesLoading } = useRecipes({ isActive: "true", project: projectId });

    const packagingTypeOptions = useMemo(() => {
        return (packagingTypesData?.data || []).map((pt) => ({
            label: pt.name,
            value: pt.id,
        }));
    }, [packagingTypesData?.data]);

    const recipeOptions = useMemo(() => {
        return (recipesData?.data || []).map((recipe) => ({
            label: `${recipe.name}${recipe.recipeCode ? ` (${recipe.recipeCode})` : ""}`,
            value: recipe._id,
            raw: recipe,
        }));
    }, [recipesData?.data]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm();

    const watchBatchSize = watch("batchSize");
    const watchPerPiece = watch("perPiece");
    const watchPacketQuantity = watch("packetQuantity");
    const watchOutput = watch("output");
    const watchWastage = watch("wastage");

    useEffect(() => {
        if (open) {
            if (sample) {
                const batchSize = sample.batchSize ?? "";
                const perPiece = sample.perPackQuantity || sample.perPiece || "";
                const packetQuantity = sample.packetQuantity || (batchSize && perPiece ? Math.floor(Number(batchSize) / Number(perPiece)) : "");
                const output = sample.output ?? (packetQuantity && perPiece ? Number(packetQuantity) * Number(perPiece) : "");
                const wastage = sample.wastage ?? (batchSize && output ? Math.max(0, Number(batchSize) - Number(output)) : "");

                reset({
                    recipe: sample.recipe?._id || sample.recipe || "",
                    batchSize: String(batchSize),
                    output: String(output),
                    packagingStatus: sample.packagingStatus || "Not Started",
                    packageType: sample.packageType?._id || sample.packageType || "",
                    perPiece: String(perPiece),
                    packetQuantity: String(packetQuantity),
                    wastage: String(wastage),
                });
            } else {
                reset({
                    recipe: recipeOptions[0]?.value || "",
                    batchSize: "",
                    output: "",
                    packagingStatus: "Not Started",
                    packageType: packagingTypeOptions[0]?.value || "",
                    perPiece: "",
                    packetQuantity: "",
                    wastage: "",
                });
            }
            setError(null);
        }
    }, [open, sample, recipeOptions, packagingTypeOptions, reset]);

    useEffect(() => {
        if (!open) return;
        const packetQuantity = parseFloat(watchPacketQuantity) || 0;
        const perPiece = parseFloat(watchPerPiece) || 0;
        const batchSize = parseFloat(watchBatchSize) || 0;
        const output = packetQuantity * perPiece;
        const wastage = Math.max(0, batchSize - output);
        const formattedOutput = String(Number(output.toFixed(2)));
        const formattedWastage = String(Number(wastage.toFixed(2)));

        if (watchOutput !== formattedOutput) setValue("output", formattedOutput);
        if (watchWastage !== formattedWastage) setValue("wastage", formattedWastage);
    }, [watchPacketQuantity, watchPerPiece, watchBatchSize, watchOutput, watchWastage, setValue, open]);

    const handleClose = (isOpen) => {
        if (!isLoading) {
            onOpenChange(isOpen);
            reset();
            setError(null);
        }
    };

    const onSubmit = async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const transformedData = {
                project: projectId,
                packageType: data.packageType,
                recipe: data.recipe,
                batchSize: parseFloat(data.batchSize) || 0,
                output: parseFloat(data.output) || 0,
                perPackQuantity: parseFloat(data.perPiece) || 0,
                packetQuantity: parseFloat(data.packetQuantity) || 0,
                wastage: parseFloat(data.wastage) || 0,
                packagingStatus: data.packagingStatus || "Not Started",
            };

            await onConfirm(transformedData);
            handleClose(false);
        } catch (err) {
            setError(err.message || `An error occurred while ${mode === "create" ? "adding" : "updating"} the sample`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={open} onOpenChange={handleClose}>
            <ModalContent className="max-w-[440px] p-6 rounded-3xl bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/40 shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {mode === "create" ? "Add Sample" : "Edit Sample"}
                    </h2>
                    <button
                        type="button"
                        onClick={() => handleClose(false)}
                        className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[75vh] overflow-y-auto custom-scrollbar pr-1">
                    {error && <div className="p-3 text-xs text-red-600 border border-red-200 rounded-xl bg-red-50">{error}</div>}

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Application Recipe</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829]">
                            <select {...register("recipe", { required: true })} disabled={recipesLoading} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white cursor-pointer">
                                <option value="" disabled>{recipesLoading ? "Loading recipes..." : "Select Recipe..."}</option>
                                {recipeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                        </div>
                        {errors.recipe && <FieldError error="Recipe is required" />}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Batch Size</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center">
                            <input type="number" {...register("batchSize", { required: true })} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white" />
                            <span className="text-xs font-medium text-gray-500 ml-2">g</span>
                        </div>
                        {errors.batchSize && <FieldError error="Batch Size is required" />}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Packaging Status</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829]">
                            <select {...register("packagingStatus")} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white cursor-pointer">
                                {packagingStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Packaging Type</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829]">
                            <select {...register("packageType", { required: true })} disabled={packagingTypesLoading} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white cursor-pointer">
                                {packagingTypeOptions.length > 0 ? packagingTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>) : <option value="">Select Packaging Type...</option>}
                            </select>
                        </div>
                        {errors.packageType && <FieldError error="Packaging Type is required" />}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Per Piece</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center">
                            <input type="number" {...register("perPiece")} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white" />
                            <span className="text-xs font-medium text-gray-500 ml-2">g</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Packet Quantity</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center">
                            <input type="number" {...register("packetQuantity")} className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white" />
                            <span className="text-xs font-medium text-gray-500 ml-2">pcs</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Lab Output</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center">
                            <input type="number" {...register("output", { required: true })} readOnly className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white cursor-default" />
                            <span className="text-xs font-medium text-gray-500 ml-2">g</span>
                        </div>
                        {errors.output && <FieldError error="Lab Output is required" />}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Wastage</label>
                        <div className="w-full px-4 py-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center">
                            <input type="number" {...register("wastage")} readOnly className="w-full bg-transparent outline-none font-bold text-xs text-gray-900 dark:text-white cursor-default" />
                            <span className="text-xs font-medium text-gray-500 ml-2">g</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-3">
                        <button type="button" onClick={() => handleClose(false)} disabled={isLoading} className="flex-1 py-3 rounded-xl border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold text-xs hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-all cursor-pointer disabled:opacity-50">Cancel</button>
                        <button type="submit" disabled={isLoading} className="flex-1 py-3 rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white font-bold text-xs transition-all shadow-sm cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
                            {isLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{mode === "create" ? "Adding..." : "Saving..."}</> : mode === "create" ? "Next" : "Confirm"}
                        </button>
                    </div>
                </form>
            </ModalContent>
        </Modal>
    );
}
